import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { GeneralParameters } from 'src/app/constants/general-parameters-constans';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { GeneralParameterTableModel } from 'src/app/models/tables/authentication/general-parameter-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { GeneralParameterService } from './../../../../services/management/general-parameter.service';

@Component({
  selector: 'app-general-parameter',
  templateUrl: './general-parameter.component.html',
  styleUrls: ['./general-parameter.component.scss'],
  standalone: false
})

export class GeneralParameterComponent implements OnInit, OnDestroy {

  ascendant: boolean = true;
  columnSort: string | null = null;
  companyId: number = 0;
  disabledButton: boolean = false;
  detailData: PagedResponseModel<GeneralParameterTableModel> = new PagedResponseModel<GeneralParameterTableModel>();
  filters: ColumnResponseModel[] = [];
  generalParameterId: number = 0;
  generalParameterName: string = '';
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  output$: Subject<any>;
  page: number = 1;
  suscription: Subscription;
  swAccessoryDispatchWithoutOrder: boolean = false;
  swEquipmentDispatchWithoutOrder: boolean = false;
  swMaintenanceOrder: boolean = false;
  swMaintenanceStrategy: boolean = false;
  swMaintenanceScheduling: boolean = false;
  swOrderModule: boolean = false;
  swQuotationsModule: boolean = false;
  swRequireQuotationForOrder: boolean = false;
  tableActions: TableAction[] = [];

  constructor(private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private generalParameterService: GeneralParameterService,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    @Optional() private ref: DynamicDialogRef) {
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.companyId = inputs.companyId;
      this.disabledButton = inputs.disabledButton;
    });
    await Promise.all([
      this.getGeneralParametersByCompany(),
      this.getColumnsGeneralParameter(),
      this.getAllGeneralParameterWithPaginationAsync(),
    ]);
    this.tableActions = !this.disabledButton ? this.getDefaultActions() : [];
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Modificar',
        icon: 'pi pi-sync',
        action: 'edit'
      },
    ];
  }

  close() {
    this.ref.close();
  }

  permissionAlerts(): boolean {
    const modulesEnabled = this.swQuotationsModule && this.swOrderModule;
    const validations = [
      {
        condition: this.generalParameterName === GeneralParameters.requireQuotationForOrder && !modulesEnabled,
        message: `Para habilitar "ObligaCotizacionPedido", debe estar activo el "ModuloPedidos" y el "ModuloCotizaciones".`
      },
      {
        condition: ((this.generalParameterName === GeneralParameters.quotationsModule && this.swQuotationsModule) ||
          (this.generalParameterName === GeneralParameters.orderModule && this.swOrderModule)) && this.swRequireQuotationForOrder,
        message: `No puede desactivar el "${this.generalParameterName}" mientras "ObligaCotizacionPedido" esté activo.`
      },
      {
        condition: !this.swOrderModule && this.generalParameterName === GeneralParameters.accessoryDispatchWithoutOrder,
        message: `No puede activar el "${this.generalParameterName}" mientras el "ModuloPedidos" esté inactivo.`
      },
      {
        condition: !this.swOrderModule && this.generalParameterName === GeneralParameters.equipmentDispatchWithoutOrder,
        message: `No puede activar el "${this.generalParameterName}" mientras el "ModuloPedidos" esté inactivo.`
      },
      {
        condition: (this.generalParameterName === GeneralParameters.orderModule && this.swOrderModule && this.swAccessoryDispatchWithoutOrder &&
          this.swEquipmentDispatchWithoutOrder),
        message: `No puede inactivar el "${this.generalParameterName}" mientras "DespachoAccesoriosSinPedido" y 
        "DespachoEquiposSinPedido" estén activos.`
      },
      {
        condition: !this.swMaintenanceOrder && this.generalParameterName === GeneralParameters.maintenanceStrategy,
        message: `No puede activar el "${this.generalParameterName}" mientras el "OrdenesDeMantenimiento" esté inactivo.`
      },
      {
        condition: !this.swMaintenanceOrder && this.generalParameterName === GeneralParameters.maintenanceScheduling,
        message: `No puede activar la "${this.generalParameterName}" mientras el "OrdenesDeMantenimiento" esté inactivo.`
      },
      {
        condition: this.swMaintenanceOrder && !this.swMaintenanceStrategy && this.generalParameterName === GeneralParameters.maintenanceScheduling,
        message: `No puede activar la "${this.generalParameterName}" mientras el "EstrategiaMantenimiento" esté inactivo.`
      },
      {
        condition: this.swMaintenanceOrder && this.swMaintenanceScheduling && this.generalParameterName === GeneralParameters.maintenanceStrategy,
        message: `No puede inactivar la "${this.generalParameterName}" mientras el "OrdenMatenimientoProgramada" esté activo.`
      },
    ];

    for (const validation of validations) {
      if (validation.condition) {
        this.toastHandlerService.showToast('warning', validation.message, 'Atención');
        return true;
      }
    }
    return false;
  }

  async getAllGeneralParameterWithPaginationAsync() {
    try {
      this.detailData = await firstValueFrom(this.generalParameterService.getAllGeneralParameterWithPaginationAsync(this.companyId, this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener los datos de los parámetros generales.', error);
    }
  }

  async getColumnsGeneralParameter() {
    try {
      this.header = await firstValueFrom(this.generalParameterService.getColumnsGeneralParameter());
    } catch (error) {
      console.error('Error al obtener las columnas para consulta de los parámetros generales.', error);
    }
  }

  async getGeneralParametersByCompany(): Promise<void> {
    try {
      const response = await firstValueFrom(this.generalParameterService.getGeneralParametersByCompany(this.companyId));
      this.userAuthenticatedService.assignListGeneralParametersByCompany(response);
      this.swOrderModule = response.some(x => x.name === GeneralParameters.orderModule);
      this.swQuotationsModule = response.some(x => x.name === GeneralParameters.quotationsModule);
      this.swEquipmentDispatchWithoutOrder = response.some(x => x.name === GeneralParameters.equipmentDispatchWithoutOrder);
      this.swAccessoryDispatchWithoutOrder = response.some(x => x.name === GeneralParameters.accessoryDispatchWithoutOrder);
      this.swRequireQuotationForOrder = response.some(x => x.name === GeneralParameters.requireQuotationForOrder);
      this.swMaintenanceOrder = response.some(x => x.name === GeneralParameters.maintenanceOrder);
      this.swMaintenanceStrategy = response.some(x => x.name === GeneralParameters.maintenanceStrategy);
      this.swMaintenanceScheduling = response.some(x => x.name === GeneralParameters.maintenanceScheduling);
    } catch (error) {
      console.error('Error al obtener los parámetros generales de la compañía.', error);
    }
  }

  async onAction(event: { action: string, item?: GeneralParameterTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.generalParameterId = event.item.id;
      this.generalParameterName = event.item.name;
      if (await this.confirmDialogService.confirm("¿Realmente desea modificar el estado de este parámetro general?")) {
        await this.savePermissions();
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllGeneralParameterWithPaginationAsync();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async savePermissions() {
    try {
      if (this.permissionAlerts()) return;
      await firstValueFrom(this.generalParameterService.saveGeneralParametersByCompany(this.companyId, this.generalParameterId));
      this.toastHandlerService.showToast('success', 'El parámetro general fue modificado exitosamente.');
      await Promise.all([
        this.getGeneralParametersByCompany(),
        this.getAllGeneralParameterWithPaginationAsync()
      ]);
    } catch (error) {
      console.error('Error al guardar el parámetro general.', error);
    }
  }
}
